import { useCallback, useEffect, useState } from "react";

/** The three switchable listing-card layouts, chosen per-browser by each
 *  employee and persisted in localStorage:
 *   - comfortable → big cover photo + full details + description (the default)
 *   - compact     → shorter photo, no description, denser grid (more per row)
 *   - list        → horizontal rows: small thumbnail + details side-by-side
 *
 *  Shared by Owner Listings, Market, and Bulk publish via ``useCardView`` so a
 *  switch on any of them applies everywhere. */
export type CardView = "comfortable" | "compact" | "list";

const STORAGE_KEY = "agencygrid.card_view.v1";
// Custom event so every mounted page re-reads the choice the instant it
// changes (the native ``storage`` event only fires in *other* tabs).
const CHANGE_EVENT = "agencygrid:cardview";
const DEFAULT_VIEW: CardView = "comfortable";
const ALL: CardView[] = ["comfortable", "compact", "list"];

function readView(): CardView {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && (ALL as string[]).includes(v)) return v as CardView;
  } catch {
    /* localStorage blocked (private mode / SSR) — fall back to default */
  }
  return DEFAULT_VIEW;
}

/** Read + set the employee's card-view preference. Backed by localStorage and
 *  kept in sync across all mounted pages (and other tabs). */
export function useCardView(): [CardView, (v: CardView) => void] {
  const [view, setView] = useState<CardView>(readView);

  useEffect(() => {
    const sync = () => setView(readView());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const update = useCallback((v: CardView) => {
    try {
      localStorage.setItem(STORAGE_KEY, v);
    } catch {
      /* ignore persistence failure — still update this session */
    }
    setView(v);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return [view, update];
}

/** Tailwind grid classes for the listing grid, per view. Compact fits more
 *  columns; list drops to 1–2 wide rows. */
export function cardGridClass(view: CardView): string {
  switch (view) {
    case "compact":
      return "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3";
    case "list":
      return "grid grid-cols-1 xl:grid-cols-2 gap-3";
    default:
      return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4";
  }
}

const OPTIONS: { id: CardView; label: string; title: string; icon: JSX.Element }[] = [
  {
    id: "comfortable",
    label: "Comfortable",
    title: "Comfortable — large cards with photo and description",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="8" height="8" rx="1.5" />
        <rect x="13" y="3" width="8" height="8" rx="1.5" />
        <rect x="3" y="13" width="8" height="8" rx="1.5" />
        <rect x="13" y="13" width="8" height="8" rx="1.5" />
      </svg>
    ),
  },
  {
    id: "compact",
    label: "Compact",
    title: "Compact — denser grid, more cards per row",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="5" height="5" rx="1" />
        <rect x="10" y="3" width="5" height="5" rx="1" />
        <rect x="17" y="3" width="4" height="5" rx="1" />
        <rect x="3" y="10" width="5" height="5" rx="1" />
        <rect x="10" y="10" width="5" height="5" rx="1" />
        <rect x="17" y="10" width="4" height="5" rx="1" />
        <rect x="3" y="17" width="5" height="4" rx="1" />
        <rect x="10" y="17" width="5" height="4" rx="1" />
        <rect x="17" y="17" width="4" height="4" rx="1" />
      </svg>
    ),
  },
  {
    id: "list",
    label: "List",
    title: "List — horizontal rows with thumbnails",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="6" height="6" rx="1" />
        <rect x="3" y="14" width="6" height="6" rx="1" />
        <path d="M12 6h9M12 9h6" />
        <path d="M12 16h9M12 19h6" />
      </svg>
    ),
  },
];

/** Segmented 3-way control for picking the card view. Icon-only on small
 *  screens; icon + label from sm+. */
export function CardViewSwitcher({
  value,
  onChange,
  className = "",
}: {
  value: CardView;
  onChange: (v: CardView) => void;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label="Card view"
      className={`inline-flex h-9 rounded-lg border border-white/10 bg-ink-900/60 p-0.5 ${className}`}
    >
      {OPTIONS.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            role="tab"
            aria-selected={active}
            title={o.title}
            onClick={() => onChange(o.id)}
            className={`px-2.5 h-8 rounded-md text-[12px] font-medium transition-colors inline-flex items-center gap-1.5 ${
              active
                ? "bg-accent-500/20 text-accent-100 border border-accent-500/40"
                : "text-slate-300 hover:text-slate-100"
            }`}
          >
            {o.icon}
            <span className="hidden sm:inline">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}
